import React, { Component, RefObject } from 'react'
import {
  addValue,
  createFilterSet,
  FilterSet,
  getLookupDisplayValue,
  LookupDisplayValue,
  removeAllValues,
  removeRedundantFilters,
  removeValue,
  resetSearch,
  SearchQuery,
  SearchQueryByFilter,
  SearchTemplate,
  setValue,
  constructAppliedQueryByFilter,
  SupportedDisplayFilterType,
} from '../../../../generic-search-common/src'
import { PillFilter } from './components/pill-filter'
import { CollapsedFilters } from './components/collapsed-filters/component'
import { FilterGroup } from './components/filter-group/component'
import { LabelledGroupCollapsedFilters } from './components/collapsed-filters/labelled-group-collapsed-filters'
import { Button, Divider } from '@msaf/core-react'
import { RequestOptions } from '@msaf/generic-search-common'

export interface FiltersProps {
  isLoading?: boolean
  isMapMode?: boolean
  searchTemplate: SearchTemplate
  searchTypeKey: string
  onSearch?: (searchQuery: SearchQuery, resetToFirstPage?: boolean) => void
  saveAction?: () => void
  actions?: { [key: string]: (...args: any[]) => void }
  requestOptions?: RequestOptions
  dateFormat: string
  searchQuery?: SearchQuery
  switchToMapView?: () => void
}

export interface FiltersState {
  isCollapsed?: boolean
  noFilters: boolean
  query: SearchQueryByFilter
  localSearchQuery: SearchQuery
  searchButtonRef?: RefObject<HTMLButtonElement>
}

const EMPTY_SEARCH_QUERY: FilterSet = { filterSet: { booleanOperator: 'AND', filters: [] } }

/**
 * This component encapsulates the core of the search filter logic
 * TODO:
 * Consider making this a functional component and break the functionalities into smaller, manageable pieces using hooks.
 */
export class Filters extends Component<FiltersProps, FiltersState> {
  private searchButtonRef: RefObject<HTMLButtonElement>

  constructor(props: FiltersProps) {
    super(props)

    const byFilter: SearchQueryByFilter = {}
    props.searchTemplate.filters.forEach((f) => {
      byFilter[f.filterKey] = []
    })

    this.state = {
      noFilters: true,
      query: byFilter,
      localSearchQuery: this.props.searchQuery || EMPTY_SEARCH_QUERY,
      isCollapsed: !!this.props.searchQuery?.filterSet?.filters.length,
    }

    this.searchButtonRef = React.createRef()
  }

  componentDidMount() {
    const { query, localSearchQuery } = this.state
    const appliedQuery = this.updateAppliedQueryByFilter(localSearchQuery, query)
    this.setState({ query: appliedQuery })
  }

  componentDidUpdate(prevProps: FiltersProps, prevState: FiltersState) {
    const { noFilters, localSearchQuery } = this.state

    // Perform search when all filters are cleared and the search query is updated
    // This will be triggered on `Reset search` or on manually clearing all the filters
    if (noFilters && JSON.stringify(localSearchQuery) !== JSON.stringify(prevState.localSearchQuery)) {
      this.performSearch(false)
    }

    // If the search query has changed, the local state is no longer valid.
    // So, reset the local state using the new search query
    if (prevProps.searchQuery?.searchTypeKey !== this.props.searchQuery?.searchTypeKey) {
      const byFilter: SearchQueryByFilter = {}
      this.props.searchTemplate.filters.forEach((f) => {
        byFilter[f.filterKey] = []
      })
      const searchQuery = this.props.searchQuery ?? EMPTY_SEARCH_QUERY
      const appliedQuery = this.updateAppliedQueryByFilter(searchQuery, byFilter)
      this.setState({
        localSearchQuery: searchQuery,
        query: appliedQuery,
        isCollapsed: !!searchQuery?.filterSet?.filters.length,
        noFilters: Object.keys(appliedQuery).filter((k) => appliedQuery[k].length > 0).length === 0,
      })
    }
  }

  /**
   * Updates the query grouped by filters by merging the values from applied search query filters
   * @param searchQuery Applied search query
   * @param queryByFilter Query grouped by filters in search template
   * @returns Query grouped by filters
   */
  updateAppliedQueryByFilter(searchQuery: SearchQuery, queryByFilter: SearchQueryByFilter) {
    let appliedQuery = queryByFilter
    if (searchQuery?.filterSet?.filters.length) {
      const appliedQueryByFilter: SearchQueryByFilter = {}
      constructAppliedQueryByFilter(searchQuery.filterSet.filters, appliedQueryByFilter)
      appliedQuery = { ...queryByFilter, ...appliedQueryByFilter }
    }
    return appliedQuery
  }

  /**
   * This gets called after changing the internal representation of the query, in order to construct:
   * 1. The searchQuery JSON
   * 2. The filter values passed to child components
   */
  queryChanged() {
    let baseQuery = createFilterSet(
      Object.keys(this.state.query).map((k) => createFilterSet(this.state.query[k], 'OR')),
      'AND',
    )

    let searchQuery = removeRedundantFilters(baseQuery, this.props.searchTemplate, true, false)

    if (searchQuery === undefined) {
      this.setState({
        localSearchQuery: createFilterSet([], 'AND'),
      })
    } else {
      if ('filterSet' in searchQuery) {
        this.setState({ localSearchQuery: searchQuery })
      } else {
        throw new Error('removeRedundantFilters failed to produce a consistent filterSet')
      }
    }

    const noFilters = Object.keys(this.state.query).filter((k) => this.state.query[k].length > 0).length === 0
    this.setState({ noFilters })
  }

  /**
   * Adds a new instance of a filter to the state to be rendered
   * @param filterKey Filter to add
   */
  addValue(filterKey: string) {
    if (filterKey in this.state.query) {
      this.setState(
        ({ query, ...rest }) => {
          const filterTemplate = this.props.searchTemplate.filters.find((f) => f.filterKey === filterKey)
          if (filterTemplate === undefined) {
            throw new Error('filterKey must be present in template')
          }

          const newQuery = addValue(
            filterKey,
            query,
            // The filter type, at this point, should only the supported display filter types
            filterTemplate.type as SupportedDisplayFilterType,
            filterTemplate.filterOperator,
          )
          return {
            ...rest,
            query: newQuery,
          }
        },
        () => {
          this.queryChanged()
        },
      )
    }
  }

  /**
   * Sets the value for a rendered filter
   * @param filterKey Filter to set
   * @param index Index of the filters rendered to set
   * @param value Value to set - must be either a string or a filterSet (in case of date-range)
   */
  setValue(filterKey: string, index: number, value: string | FilterSet) {
    this.setState(
      ({ query, ...rest }) => {
        const newQuery = setValue(filterKey, index, value, query)
        return {
          ...rest,
          query: newQuery,
        }
      },
      () => this.queryChanged(),
    )
  }

  /**
   * Removes a filter from array, dropping down the items rendered
   * @param filterKey Filter to update
   * @param index Index of filter to remove
   */
  removeValue(filterKey: string, index: number) {
    this.setState(
      ({ query, ...rest }) => {
        const newQuery = removeValue(filterKey, index, query)
        return {
          ...rest,
          query: newQuery,
        }
      },
      () => this.queryChanged(),
    )
  }

  /**
   * Resets search to the base state - no filters applied or listed
   */
  resetSearch() {
    this.setState(
      ({ query, ...rest }) => {
        const newQuery = resetSearch(query)
        return {
          ...rest,
          query: newQuery,
        }
      },
      () => this.queryChanged(),
    )
  }

  /**
   * Resets a given filter - removing all active values and setting the pill
   * @param filterKey Filter to reset
   */
  removeAllValues(filterKey: string) {
    this.setState(
      ({ query, ...rest }) => {
        const newQuery = removeAllValues(filterKey, query)
        return {
          ...rest,
          query: newQuery,
        }
      },
      () => this.queryChanged(),
    )
  }

  /**
   * Performs the search with the currently set filters
   * `isCollapseFilters` collapses the filters on search, by default
   */
  performSearch(isCollapseFilters: boolean = true) {
    this.props.onSearch && this.props.onSearch(this.state.localSearchQuery, false)
    this.setState({ isCollapsed: isCollapseFilters })
  }

  switchToMapView() {
    const { switchToMapView: openMapView } = this.props
    if (openMapView) {
      openMapView()
    }
  }

  /**
   * Fetches the display label for a given lookup value
   */
  lookupResolver = async (value: string, optionsKey?: string, searchTypeKey?: string): Promise<string> => {
    try {
      const result: LookupDisplayValue = await getLookupDisplayValue(
        {
          searchTypeKey,
          optionsKey,
          internalValue: value,
        },
        this.props.requestOptions,
      )

      return result?.lookupDisplayValueResult?.displayValue
    } catch (e: any) {
      // TODO: Add proper error handling
      throw Error(e)
    }
  }

  handleKeyboardEvent: React.KeyboardEventHandler = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      this.searchButtonRef.current?.focus()
    }
  }

  render() {
    const { isCollapsed, noFilters, query } = this.state
    const {
      isLoading,
      actions,
      isMapMode,
      onSearch,
      saveAction,
      searchTemplate,
      searchTypeKey,
      requestOptions,
      dateFormat,
    } = this.props

    return (
      <div>
        {isCollapsed ? (
          <div className='generic-search-filter__pills'>
            {isMapMode ? (
              <LabelledGroupCollapsedFilters
                modifyValues={() => this.setState({ isCollapsed: false })}
                query={query}
                searchTemplate={searchTemplate}
                lookupResolver={this.lookupResolver}
              />
            ) : (
              <CollapsedFilters
                modifyValues={() => this.setState({ isCollapsed: false })}
                query={query}
                searchTemplate={searchTemplate}
                lookupResolver={this.lookupResolver}
              />
            )}

            <div className='generic-search-filter__actions'>
              <Button
                label='Modify search'
                data-selenium-search-modify
                onClick={() => this.setState({ isCollapsed: false })}
                buttonStyle='text-action'
                className='generic-search-filter__action-btn'
              />
              {!searchTemplate.savedSearchDisabled && saveAction && (
                <Button
                  label='Save search'
                  onClick={() => saveAction()}
                  buttonStyle='text-action'
                  className='generic-search-filter__action-btn'
                />
              )}
            </div>
          </div>
        ) : (
          <>
            <div className='generic-search-filter__pills'>
              {searchTemplate.filters.map((filter) => {
                const key = filter.filterKey
                const enabled = !!query[key]?.length
                return (
                  <PillFilter
                    key={key}
                    isEnabled={enabled}
                    addValue={() => this.addValue(key)}
                    removeAllValues={() => this.removeAllValues(key)}
                    label={filter.label}
                    isSkeleton={isLoading}
                  />
                )
              })}
            </div>

            <Divider isFullWidth verticalSpacingSize='small' />

            {noFilters && (
              <div className='generic-search-filter__help-text'>
                Select from the above criteria to refine your search
              </div>
            )}

            <div
              className={`generic-search-filter__inputs ${isMapMode ? 'generic-search-filter__inputs--map-mode' : ''}`}
            >
              {Object.entries(this.state.query).map(([filter, query]) => (
                <FilterGroup
                  key={filter}
                  filterKey={filter}
                  searchTemplate={searchTemplate}
                  query={query}
                  addValue={() => this.addValue(filter)}
                  setValue={(index: number, value: string | FilterSet) => this.setValue(filter, index, value)}
                  removeValue={(index: number) => this.removeValue(filter, index)}
                  search={() => this.performSearch()}
                  searchTypeKey={searchTypeKey}
                  requestOptions={requestOptions}
                  dateFormat={dateFormat}
                  handleKeyboardEvent={this.handleKeyboardEvent}
                />
              ))}
            </div>

            <div className='generic-search-filter__search-actions'>
              {searchTemplate.searchActions &&
                searchTemplate.searchActions.map((action) => {
                  if (!(action.hideForMapMode && isMapMode)) {
                    const actionName = action.action

                    let onClick = actions && actions[action.action]

                    // OnClick for performSearch, etc.
                    if (!onClick && actionName in this) {
                      // Needs to be an arrow function to inherit context from parent
                      onClick = (...args: any[]) => (this[actionName as keyof Filters] as Function)(...args)
                    }

                    const args = (action.args || []).map((a) => {
                      if (a in this.state) {
                        return this.state[a as keyof FiltersState]
                      } else if (a in this.props) {
                        return this.props[a as keyof FiltersProps]
                      }
                      return undefined
                    })

                    return (
                      <Button
                        label={action.label}
                        ref={action.label === 'Search' ? this.searchButtonRef : undefined}
                        key={action.label}
                        onClick={() => onClick && onClick(...args)}
                        onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) =>
                          e.key === 'Enter' && onClick && onClick(...args)
                        }
                        buttonStyle={action.isPrimary ? 'primary' : 'text-action'}
                        className='generic-search-filter__search-action'
                        aria-label={action.label}
                      />
                    )
                  }
                  return null
                })}
              {!searchTemplate.searchActions && (
                <>
                  {onSearch && (
                    <Button
                      label='Search'
                      data-selenium-search-perform
                      aria-label='Search'
                      onClick={() => this.performSearch()}
                      onKeyDown={() => this.performSearch()}
                      buttonStyle='primary'
                    />
                  )}
                  <Button
                    label='Reset'
                    aria-label='Search'
                    data-test-search-reset
                    onClick={() => this.resetSearch()}
                    onKeyDown={() => this.resetSearch()}
                    buttonStyle='text-action'
                  />
                </>
              )}
            </div>
          </>
        )}
      </div>
    )
  }
}
